import { CloseOutlined, PushpinOutlined, ReloadOutlined } from "@ant-design/icons";
import { App, ConfigProvider, Dropdown, Tabs, type MenuProps } from "antd";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate, useRoutes, type RouteObject } from "react-router";
import { useI18n } from "@/i18n";
import { PageErrorBoundary } from "../ErrorBoundary";
import { HOME, navFor } from "../nav";

/**
 * Work tabs, ERP style: every page opened from the menu becomes a tab, and a tab
 * keeps its page mounted while hidden, so filters, scroll position, open drawers
 * and half-filled forms survive switching back and forth.
 *
 * How: each open tab renders the app's page routes against its own location
 * (useRoutes with a location argument), so every tab is a full page with its
 * own URL, search params and state. Only the active pane is visible. Popups
 * (modals, drawers, dropdowns) render inside their tab's pane, so a hidden tab's
 * open drawer is hidden with it. A restored tab mounts on first activation.
 *
 * Detail pages (an order, a wave, a task) open as a preview tab, as in an IDE:
 * opening the next detail from the same menu replaces it instead of piling up
 * tabs. Double-clicking the tab, or "Pertahankan tab", keeps it open.
 *
 * The tab list (not page state) is kept in sessionStorage so a reload reopens the
 * same tabs; each page then loads fresh.
 *
 * The window scrolls, not the pane, so each tab's scroll position is kept here
 * and put back when it shows again; a newly opened page starts at the top.
 */

export const MAX_TABS = 20;
const STORAGE_KEY = "zerpid.tabs.v1";

export type Tab = {
  /** pathname; also the route the tab returns to */
  key: string;
  /** full location (pathname + search) last seen in this tab */
  href: string;
  lastUsed: number;
  /** bumping it remounts the page ("Muat ulang tab") */
  version: number;
  /** false for tabs restored after a reload until they are first opened */
  visited: boolean;
  /** a detail page the next detail of the same menu replaces */
  preview?: boolean;
};

type TabsAPI = {
  setTitle: (key: string, title: string | null) => void;
};

const TabsContext = createContext<TabsAPI | null>(null);
const TabKeyContext = createContext<string>(HOME);

/** Lets a page name its tab (e.g. an order number on a detail page). */
export function useTabTitle(title: string | null | undefined) {
  const api = useContext(TabsContext);
  const key = useContext(TabKeyContext);
  useEffect(() => {
    if (!api || title === undefined) return;
    api.setTitle(key, title);
    return () => api.setTitle(key, null);
  }, [api, key, title]);
}

/** True when the calling page is the visible tab. Hidden pages can pause polling with it. */
/**
 * A refresh interval that runs only while its tab is open: hidden tabs stay
 * mounted (filters, scroll) but do not poll. Outside the tabs it always runs.
 */
export function usePoll(ms: number): number | false {
  const api = useContext(TabsContext);
  const key = useContext(TabKeyContext);
  const path = useLocation().pathname;
  return !api || path === key ? ms : false;
}

export function useIsActiveTab(): boolean {
  const key = useContext(TabKeyContext);
  return useLocation().pathname === key;
}

/** The menu a detail page belongs to (/app/orders for /app/orders/123); null for menu pages. */
export function detailGroup(pathname: string): string | null {
  const nav = navFor(pathname);
  return nav && nav.key !== pathname ? nav.key : null;
}

/**
 * Pure: focus the tab for key (or open it), and when over the limit close the
 * least recently used tab that is neither home nor the one being opened.
 * A new detail page takes over the preview tab of its menu, if there is one.
 * lastUsed is a sequence number, not a time, so this stays deterministic.
 */
export function openTab(prev: Tab[], key: string, href: string): { tabs: Tab[]; evicted: boolean } {
  const seq = Math.max(0, ...prev.map((x) => x.lastUsed)) + 1;
  const group = detailGroup(key);
  const opened: Tab = { key, href, lastUsed: seq, version: 0, visited: true, preview: group !== null };
  const slot = group === null ? undefined : prev.find((x) => x.preview && detailGroup(x.key) === group);
  let tabs = prev.some((x) => x.key === key)
    ? prev.map((x) => (x.key === key ? { ...x, href, lastUsed: seq, visited: true } : x))
    : slot
      ? prev.map((x) => (x === slot ? opened : x))
      : [...prev, opened];
  let evicted = false;
  if (tabs.length > MAX_TABS) {
    const victim = tabs.filter((x) => x.key !== HOME && x.key !== key).sort((a, b) => a.lastUsed - b.lastUsed)[0];
    if (victim) {
      tabs = tabs.filter((x) => x.key !== victim.key);
      evicted = true;
    }
  }
  return { tabs, evicted };
}

function loadTabs(): Tab[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { key: string; href: string; preview?: boolean }[];
    return parsed
      .filter((t) => typeof t.key === "string" && t.key.startsWith("/app"))
      .map((t, i) => ({ key: t.key, href: t.href, lastUsed: i, version: 0, visited: false, preview: t.preview === true }));
  } catch {
    return [];
  }
}

function saveTabs(tabs: Tab[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tabs.map(({ key, href, preview }) => ({ key, href, preview }))));
  } catch {
    // storage blocked: tabs just won't survive a reload
  }
}

export function WorkTabs({ routes, banner }: { routes: RouteObject[]; banner?: ReactNode }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const active = location.pathname;

  const [tabs, setTabs] = useState<Tab[]>(() => {
    const restored = loadTabs();
    return restored.some((x) => x.key === HOME)
      ? restored
      : [{ key: HOME, href: HOME, lastUsed: 0, version: 0, visited: false }, ...restored];
  });
  const [titles, setTitles] = useState<Record<string, string>>({});

  // Open or focus the tab for the current location. Done during render (React's
  // "adjust state when a prop changes" pattern) so the tab bar and the page
  // switch in the same paint.
  const locKey = location.pathname + location.search;
  const [seenLoc, setSeenLoc] = useState<string | null>(null);
  const [evictions, setEvictions] = useState(0);
  if (seenLoc !== locKey) {
    setSeenLoc(locKey);
    const result = openTab(tabs, active, locKey);
    setTabs(result.tabs);
    if (result.evicted) setEvictions((n) => n + 1);
  }
  useEffect(() => {
    if (evictions > 0) void message.info(t("tabs.autoClosed", { max: MAX_TABS }));
  }, [evictions, message, t]);

  useEffect(() => saveTabs(tabs), [tabs]);

  // Scroll per tab: remember where each one was, restore it on return.
  const scrolls = useRef(new Map<string, number>());
  const shown = useRef(active);
  useEffect(() => {
    const onScroll = () => scrolls.current.set(shown.current, window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useLayoutEffect(() => {
    if (shown.current === active) return;
    shown.current = active;
    const y = scrolls.current.get(active) ?? 0;
    if (window.scrollY !== y) window.scrollTo(0, y);
  }, [active]);

  const go = useCallback((tab: Tab) => navigate(tab.href), [navigate]);

  const close = useCallback(
    (keys: string[]) => {
      const closing = new Set(keys.filter((k) => k !== HOME));
      if (closing.size === 0) return;
      setTabs((prev) => {
        const remaining = prev.filter((x) => !closing.has(x.key));
        if (closing.has(active)) {
          // Same as a browser: the neighbour to the left, else the most recent.
          const idx = prev.findIndex((x) => x.key === active);
          const fallback = remaining[Math.max(0, Math.min(idx - 1, remaining.length - 1))] ?? remaining[0];
          if (fallback) queueMicrotask(() => navigate(fallback.href));
        }
        return remaining;
      });
      setTitles((prev) => {
        const next = { ...prev };
        closing.forEach((k) => delete next[k]);
        return next;
      });
    },
    [active, navigate],
  );

  const reload = useCallback((key: string) => {
    setTabs((prev) => prev.map((x) => (x.key === key ? { ...x, version: x.version + 1 } : x)));
  }, []);

  const keep = useCallback((key: string) => {
    setTabs((prev) => prev.map((x) => (x.key === key && x.preview ? { ...x, preview: false } : x)));
  }, []);

  const api = useMemo<TabsAPI>(
    () => ({
      setTitle: (key, title) =>
        setTitles((prev) => {
          if (title === null) {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          }
          return prev[key] === title ? prev : { ...prev, [key]: title };
        }),
    }),
    [],
  );

  const label = (tab: Tab) => {
    const nav = navFor(tab.key);
    return titles[tab.key] ?? (nav ? t(nav.label) : tab.key);
  };

  const menuFor = (tab: Tab): MenuProps["items"] => {
    const idx = tabs.findIndex((x) => x.key === tab.key);
    return [
      { key: "reload", icon: <ReloadOutlined />, label: t("tabs.reload") },
      ...(tab.preview ? [{ key: "keep", icon: <PushpinOutlined />, label: t("tabs.keep") }] : []),
      { type: "divider" },
      { key: "close", icon: <CloseOutlined />, label: t("tabs.close"), disabled: tab.key === HOME },
      { key: "others", label: t("tabs.closeOthers"), disabled: tabs.length <= 2 && tab.key !== HOME },
      { key: "right", label: t("tabs.closeRight"), disabled: idx === tabs.length - 1 },
      { key: "all", label: t("tabs.closeAll"), disabled: tabs.length <= 1 },
    ];
  };

  const onMenu = (tab: Tab, action: string) => {
    const idx = tabs.findIndex((x) => x.key === tab.key);
    switch (action) {
      case "reload":
        reload(tab.key);
        break;
      case "keep":
        keep(tab.key);
        break;
      case "close":
        close([tab.key]);
        break;
      case "others":
        close(tabs.filter((x) => x.key !== tab.key).map((x) => x.key));
        if (tab.key !== active) go(tab);
        break;
      case "right":
        close(tabs.slice(idx + 1).map((x) => x.key));
        break;
      case "all":
        close(tabs.map((x) => x.key));
        navigate(HOME);
        break;
    }
  };

  return (
    <TabsContext.Provider value={api}>
      <div className="z-worktabs" role="presentation">
        <Tabs
          type="editable-card"
          hideAdd
          size="small"
          activeKey={active}
          onChange={(key) => {
            const tab = tabs.find((x) => x.key === key);
            if (tab) go(tab);
          }}
          onEdit={(key, action) => {
            if (action === "remove" && typeof key === "string") close([key]);
          }}
          items={tabs.map((tab) => ({
            key: tab.key,
            closable: tab.key !== HOME,
            label: (
              <Dropdown trigger={["contextMenu"]} menu={{ items: menuFor(tab), onClick: ({ key }) => onMenu(tab, key) }}>
                <span
                  className={tab.preview ? "z-worktab-label z-worktab-preview" : "z-worktab-label"}
                  title={tab.preview ? t("tabs.previewHelp") : undefined}
                  onDoubleClick={() => keep(tab.key)}
                  onAuxClick={(e) => {
                    // Middle click closes, as in a browser.
                    if (e.button === 1 && tab.key !== HOME) {
                      e.preventDefault();
                      close([tab.key]);
                    }
                  }}
                >
                  {label(tab)}
                </span>
              </Dropdown>
            ),
          }))}
        />
      </div>
      {banner}

      {tabs.map((tab) =>
        tab.visited || tab.key === active ? (
          <TabPane
            key={`${tab.key}#${tab.version}`}
            tabKey={tab.key}
            // The active tab follows the live location; a hidden tab keeps the
            // location it was left at.
            href={tab.key === active ? location.pathname + location.search : tab.href}
            routes={routes}
            active={tab.key === active}
          />
        ) : null,
      )}
    </TabsContext.Provider>
  );
}

function TabPane({ tabKey, href, routes, active }: { tabKey: string; href: string; routes: RouteObject[]; active: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const page = useRoutes(routes, href);
  // Popups render inside the pane, so they hide with it.
  const container = useCallback(() => ref.current ?? document.body, []);
  return (
    <div ref={ref} className="z-worktab-pane" hidden={!active} style={{ position: "relative" }} aria-hidden={!active}>
      <TabKeyContext.Provider value={tabKey}>
        <ConfigProvider getPopupContainer={container}>
          <PageErrorBoundary>{page}</PageErrorBoundary>
        </ConfigProvider>
      </TabKeyContext.Provider>
    </div>
  );
}
